import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { workspaceService } from '../../lib/resellerService';

const ResellerPortal = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [stats, setStats] = useState({ totalWebsites: 0, activeWebsites: 0, totalMembers: 0, admins: 0, editors: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    loadWorkspace();
  }, []);

  const loadWorkspace = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: wsError } = await workspaceService?.getMyWorkspace();
      if (wsError) throw wsError;
      setWorkspace(data);
      if (data?.id) {
        const { data: statsData } = await workspaceService?.getStats(data?.id);
        if (statsData) setStats(statsData);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Websites', value: stats?.totalWebsites, icon: 'Globe', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'Active Websites', value: stats?.activeWebsites, icon: 'CheckCircle', color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'Team Members', value: stats?.totalMembers, icon: 'Users', color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { label: 'Admins', value: stats?.admins, icon: 'Shield', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  ];

  const quickActions = [
    { label: 'Manage Websites', icon: 'Globe', path: '/reseller-websites', description: 'Create and manage client websites' },
    { label: 'Manage Clients', icon: 'Users', path: '/reseller-clients', description: 'Invite and manage your team members' },
  ];

  return (
    <div className="app-layout">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
      <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header
          userName={userProfile?.fullName || userProfile?.full_name || 'Reseller'}
          userRole={userProfile?.role || 'reseller'}
        />
        <main className="content-area">
          <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="Building2" size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Reseller Portal</h1>
                  <p className="text-sm text-muted-foreground">Welcome back, {userProfile?.fullName || userProfile?.full_name || 'Reseller'}</p>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Icon name="Loader2" size={32} className="text-primary animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
                <Icon name="AlertCircle" size={24} className="text-destructive mx-auto mb-2" />
                <p className="text-destructive text-sm">{error}</p>
                <button onClick={loadWorkspace} className="mt-3 text-sm text-primary hover:underline">Try again</button>
              </div>
            ) : (
              <>
                {/* Workspace Info */}
                {workspace ? (
                  <div className="bg-card border border-border rounded-xl p-6 mb-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Icon name="Building2" size={24} className="text-primary" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-foreground">{workspace?.name}</h2>
                          <p className="text-sm text-muted-foreground">{workspace?.description || 'Your reseller workspace'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${workspace?.isActive ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${workspace?.isActive ? 'bg-green-400' : 'bg-red-400'}`} />
                              {workspace?.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {workspace?.customDomain && (
                              <span className="text-xs text-muted-foreground">{workspace?.customDomain}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <p>Max Websites: <span className="text-foreground font-medium">{workspace?.maxWebsites}</span></p>
                        <p>Max Users: <span className="text-foreground font-medium">{workspace?.maxUsers}</span></p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-xl p-8 mb-6 text-center">
                    <Icon name="Building2" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-foreground mb-1">No Workspace Found</h3>
                    <p className="text-sm text-muted-foreground">Contact your Super Admin to set up your reseller workspace.</p>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {statCards?.map((card) => (
                    <div key={card?.label} className="bg-card border border-border rounded-xl p-5">
                      <div className={`w-10 h-10 rounded-lg ${card?.bg} flex items-center justify-center mb-3`}>
                        <Icon name={card?.icon} size={20} className={card?.color} />
                      </div>
                      <p className="text-2xl font-bold text-foreground">{card?.value}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{card?.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-foreground mb-3">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {quickActions?.map((action) => (
                      <button
                        key={action?.path}
                        onClick={() => navigate(action?.path)}
                        className="bg-card border border-border rounded-xl p-5 text-left hover:border-primary/50 hover:bg-primary/5 transition-all group"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <Icon name={action?.icon} size={20} className="text-primary" />
                          </div>
                          <span className="font-semibold text-foreground">{action?.label}</span>
                          <Icon name="ArrowRight" size={16} className="text-muted-foreground ml-auto group-hover:text-primary transition-colors" />
                        </div>
                        <p className="text-sm text-muted-foreground pl-13">{action?.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Capacity */}
                {workspace && (
                  <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-base font-semibold text-foreground mb-4">Workspace Capacity</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className="text-muted-foreground">Websites</span>
                          <span className="text-foreground font-medium">{stats?.totalWebsites} / {workspace?.maxWebsites}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${Math.min((stats?.totalWebsites / (workspace?.maxWebsites || 1)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className="text-muted-foreground">Team Members</span>
                          <span className="text-foreground font-medium">{stats?.totalMembers} / {workspace?.maxUsers}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full transition-all"
                            style={{ width: `${Math.min((stats?.totalMembers / (workspace?.maxUsers || 1)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResellerPortal;
